import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, Truck, AlertTriangle, PackageCheck, Target, Coins, MessageSquare, Handshake, RefreshCw, Loader2, ChevronRight, FileText } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

/* Iter 322 — Tableau de bord partenaires (activité en direct par société). */
const LEVEL_CLS = {
  danger: "border-red-500/30 bg-red-500/10 text-red-300",
  warn: "border-amber-500/30 bg-amber-500/10 text-amber-300",
  info: "border-sky-500/30 bg-sky-500/10 text-sky-300",
  success: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
};

export function usePartnerDashboard(societeId, intervalMs = 60000) {
  const { axiosAuth } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    try {
      const r = await axiosAuth().get("/partners/dashboard", { params: societeId ? { societe_id: societeId } : {} });
      setData(r.data);
    } catch { /* silencieux */ }
    setLoading(false);
  // eslint-disable-next-line
  }, [societeId]);
  useEffect(() => {
    load();
    const t = setInterval(load, intervalMs);
    return () => clearInterval(t);
  }, [load, intervalMs]);
  return { data, loading, reload: load };
}

function Kpi({ icon: Icon, label, value, tone = "text-white", testId, sub }) {
  return (
    <div className="bg-[#0b0811] border border-white/5 rounded-xl p-3 flex items-center gap-3" data-testid={testId}>
      <div className={"w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center " + tone}><Icon className="w-4 h-4" /></div>
      <div className="min-w-0">
        <div className={"text-xl font-black leading-none " + tone}>{value}</div>
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold truncate">{label}</div>
        {sub && <div className="text-[10px] text-slate-500">{sub}</div>}
      </div>
    </div>
  );
}

export function PartnerDashboard({ societeId, compact = false, onOpenPartner }) {
  const { data, loading, reload } = usePartnerDashboard(societeId, compact ? 90000 : 60000);
  const k = data?.kpis || {};
  const alerts = data?.alerts || [];

  return (
    <div className="bg-[#14101c] border border-white/10 rounded-2xl p-4 md:p-5" data-testid="partner-dashboard">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Handshake className="w-4 h-4 text-[#f7c935]" />
          <div>
            <div className="text-sm font-black text-white">Activité partenaires</div>
            <div className="text-[10px] text-slate-500">{data?.generated_at ? `Mis à jour ${new Date(data.generated_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })} · rafraîchi automatiquement` : "Chargement…"}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {compact && <Link to="/admin/partenaires" className="text-[11px] text-[#f7c935] font-bold hover:underline flex items-center" data-testid="partner-dashboard-link">Gérer <ChevronRight className="w-3 h-3" /></Link>}
          <button onClick={reload} className="text-slate-500 hover:text-white" title="Rafraîchir" data-testid="partner-dashboard-refresh">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}</button>
        </div>
      </div>

      <div className={"grid gap-2 " + (compact ? "grid-cols-2" : "grid-cols-2 md:grid-cols-4 lg:grid-cols-7")}>
        <Kpi icon={ShoppingCart} label="Commandes à confirmer" value={k.orders_to_confirm ?? "–"} tone={k.orders_to_confirm ? "text-amber-400" : "text-white"} testId="pd-kpi-orders-to-confirm" />
        <Kpi icon={Truck} label="En transit" value={k.orders_in_transit ?? "–"} tone="text-sky-300" testId="pd-kpi-orders-in-transit" sub={k.orders_delivered_unreceived ? `${k.orders_delivered_unreceived} livrée(s) à réceptionner` : null} />
        <Kpi icon={AlertTriangle} label="Missions en retard" value={k.missions_late ?? "–"} tone={k.missions_late ? "text-red-400" : "text-white"} testId="pd-kpi-missions-late" sub={k.missions_in_progress != null ? `${k.missions_in_progress} en cours` : null} />
        <Kpi icon={PackageCheck} label="Livrables à valider" value={k.missions_to_validate ?? "–"} tone={k.missions_to_validate ? "text-sky-300" : "text-white"} testId="pd-kpi-missions-to-validate" />
        <Kpi icon={Target} label="Nouveaux leads" value={k.new_leads ?? "–"} tone={k.new_leads ? "text-emerald-400" : "text-white"} testId="pd-kpi-new-leads" />
        {!compact && <Kpi icon={Coins} label="Commissions en attente" value={k.commissions_pending ?? "–"} tone="text-amber-300" testId="pd-kpi-commissions" sub={k.commissions_pending_amount ? `${k.commissions_pending_amount.toLocaleString("fr-FR")} €` : null} />}
        {!compact && <Kpi icon={FileText} label="Factures déposées" value={k.invoices_pending ?? "–"} tone="text-sky-300" testId="pd-kpi-invoices" sub={`${k.messages_7d || 0} msg / 7 j`} />}
      </div>

      {!compact && (
        <div className="grid lg:grid-cols-3 gap-4 mt-4">
          <div className="lg:col-span-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">À traiter</div>
            {alerts.length === 0 ? (
              <div className="text-xs text-slate-500 italic border border-dashed border-white/10 rounded-xl p-4 text-center" data-testid="pd-alerts-empty">Rien à traiter — tout est à jour.</div>
            ) : (
              <div className="space-y-1.5" data-testid="pd-alerts">
                {alerts.map((a, i) => (
                  <button key={a.kind + (a.ref || i)} onClick={() => onOpenPartner?.(a.partner_id)} className={"w-full text-left flex items-center gap-3 px-3 py-2 rounded-lg border text-xs " + (LEVEL_CLS[a.level] || LEVEL_CLS.info)} data-testid={"pd-alert-" + a.kind}>
                    <span className="flex-1 min-w-0"><span className="font-bold">{a.title}</span>{a.subtitle && <span className="opacity-70"> · {a.subtitle}</span>}</span>
                    {a.at && <span className="text-[10px] opacity-60 shrink-0">{String(a.at).slice(0, 10)}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2 flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Derniers messages</div>
            {(data?.recent_messages || []).length === 0 ? (
              <div className="text-xs text-slate-500 italic">Aucun message de partenaire cette semaine.</div>
            ) : (
              <div className="space-y-1.5" data-testid="pd-messages">
                {data.recent_messages.map((m) => (
                  <button key={m.message_id} onClick={() => onOpenPartner?.(m.partner_id)} className="w-full text-left bg-[#0b0811] border border-white/5 rounded-lg px-3 py-2" data-testid={"pd-msg-" + m.message_id}>
                    <div className="text-[10px] text-slate-500 flex justify-between"><span className="font-bold text-slate-300">{m.partner_name}</span><span>{new Date(m.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span></div>
                    <div className="text-xs text-white truncate">{m.body}</div>
                  </button>
                ))}
              </div>
            )}
            <div className="mt-3 grid grid-cols-2 gap-1.5">
              {(data?.by_type || []).map((t) => (
                <div key={t.type} className="bg-[#0b0811] border border-white/5 rounded-lg px-2.5 py-1.5" data-testid={"pd-type-" + t.type}>
                  <div className="text-[10px] text-slate-500 truncate">{t.label}</div>
                  <div className="text-xs text-white font-bold">{t.active} actif{t.active > 1 ? "s" : ""}{t.invited ? <span className="text-amber-400 font-normal"> · {t.invited} invité{t.invited > 1 ? "s" : ""}</span> : null}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PartnerDashboard;
